import React from "react";
import PropTypes from "prop-types";
import { TbArrowUpRight, TbMinus } from "react-icons/tb";
import styles from "./MetricCard.module.css";

/**
 * Cartão de métrica reutilizável da Visão geral.
 *
 * Anatomia (design system Nexus): ícone em quadrado colorido com fundo
 * semântico → rótulo `--nx-muted` → número grande (Nunito Sans 800) → delta
 * com seta. Suporta um estado vazio elegante (`empty`) para métricas ainda
 * sem fonte de dados (placeholder discreto, sem inventar números).
 *
 * As famílias semânticas (`tone`) mapeiam para os pares de token `*-bg` / cor
 * cheia já definidos em `styles/tokens.css` — nunca cores soltas.
 */
const MetricCard = ({
  icon,
  label,
  value,
  delta,
  deltaTrend = "up",
  tone = "primary",
  loading = false,
  empty = false,
  emptyHint = "Em breve",
}) => {
  const toneClass = styles[`tone_${tone}`] || styles.tone_primary;

  if (loading) {
    return (
      <article className={styles.card} aria-busy="true">
        <span className={`${styles.iconBox} ${toneClass}`} aria-hidden="true">
          {icon}
        </span>
        <span className={styles.label}>{label}</span>
        <span className={`${styles.skeleton} ${styles.skeletonValue}`} />
        <span className={`${styles.skeleton} ${styles.skeletonDelta}`} />
      </article>
    );
  }

  return (
    <article className={styles.card}>
      <span className={`${styles.iconBox} ${toneClass}`} aria-hidden="true">
        {icon}
      </span>
      <span className={styles.label}>{label}</span>

      {empty ? (
        <>
          <span className={styles.valueEmpty} aria-label="Sem dados disponíveis">
            —
          </span>
          <span className={styles.emptyHint}>{emptyHint}</span>
        </>
      ) : (
        <>
          <span className={styles.value}>{value}</span>
          {delta ? (
            <span
              className={`${styles.delta} ${
                deltaTrend === "up" ? styles.deltaUp : styles.deltaFlat
              }`}
            >
              {deltaTrend === "up" ? (
                <TbArrowUpRight aria-hidden="true" />
              ) : (
                <TbMinus aria-hidden="true" />
              )}
              {delta}
            </span>
          ) : (
            <span className={styles.deltaSpacer} aria-hidden="true" />
          )}
        </>
      )}
    </article>
  );
};

MetricCard.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  delta: PropTypes.string,
  deltaTrend: PropTypes.oneOf(["up", "flat"]),
  tone: PropTypes.oneOf(["primary", "success", "warning", "whats"]),
  loading: PropTypes.bool,
  empty: PropTypes.bool,
  emptyHint: PropTypes.string,
};

export default MetricCard;
